import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/ui/collapsible";
import { Plug, Clock, ChevronDown } from "lucide-react";
import { MeterReading, MeterData, PaginatedResponse } from '@features/cx/consumerManagement/types';
import { useUtilityConnections, useMeterReadings } from '@features/cx/consumerManagement/hooks';
import { GenericTable } from '@shared/components/GenericTable';
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from '@shared/utils/constants';

interface ConnectionsTabProps {
  consumerNo: string;
  remoteUtilityId: string;
}

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({ consumerNo, remoteUtilityId }) => {
  const { 
    data: connections = [], 
    isLoading: loading, 
    error,
    refetch: refetchConnections
  } = useUtilityConnections(consumerNo, remoteUtilityId);

  const refetch = () => {
    refetchConnections();
  };


  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <button 
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br to-indigo-50 border shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            Utility Connections
          </CardTitle>
        </CardHeader>
        <CardContent>
          {connections.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Plug className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">No mappings available</p>
              <p className="text-sm mt-1">No utility connections found for this consumer.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {connections
                .filter((connection) => connection.remoteMeterId !== null)
                .map((connection) => (
                <ConnectionCard 
                  key={`connection-${connection.meterDetails.deviceNo}-${connection.meterDetails.meterTypeDisplay}`} 
                  connection={connection} 
                  consumerNo={consumerNo}
                  remoteUtilityId={Number(remoteUtilityId)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Sub-component for individual connection
interface ConnectionCardProps {
  connection: MeterData;
  consumerNo  : string;
  remoteUtilityId: number;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({ connection, consumerNo, remoteUtilityId }) => {
  return (
    <div className="p-5 rounded-lg bg-white shadow-sm border">
      <div className="flex items-center justify-between mb-4 pb-2 border-b">
        <h3 className="text-lg font-medium">{connection.meterDetails.productCode} Service</h3>
        {/* <Badge className="bg-green-100 text-green-800 border border-green-200">{connection.statusDisplay}</Badge> */}
      </div>
      
      <MeterCard 
          key={`${connection.meterDetails.meterNumber}-meter-${connection.meterDetails.deviceNo}`} 
          meter={connection.meterDetails} 
          consumerNo={consumerNo}
          connectionType={connection.meterDetails.meterTypeDisplay}
          remoteUtilityId={Number(remoteUtilityId)}
        />
    </div>
  );
};

// Sub-component for meter details
interface MeterCardProps {
  meter: any;
  consumerNo: string;
  connectionType: string;
  remoteUtilityId: number;
}

const MeterCard: React.FC<MeterCardProps> = ({ meter, consumerNo, connectionType, remoteUtilityId }) => {
  const [readingsOpen, setReadingsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  
  const { 
    data: readings = [], 
    isLoading: loadingReadings,
    refetch: refetchReadings
    } = useMeterReadings(
    consumerNo, 
    meter.id, 
    { page: currentPage, limit: PAGE_SIZE },
    Number(remoteUtilityId),
    hasBeenClicked // Only enable the query when section has been clicked
    );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when readings section is opened and trigger API call
  const handleReadingsToggle = (open: boolean) => {
    setReadingsOpen(open);
    if (open) {
      console.log('MeterReadingsSection clicked - triggering API call for meter:', meter.meterNumber);
      setCurrentPage(DEFAULT_PAGE_NUMBER);
      setHasBeenClicked(true); // Mark as clicked to enable API calls
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="p-4 rounded-md border">
        <h4 className="font-medium mb-3 border-b pb-2 flex items-center">
          <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M2 12h20M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"/>
          </svg>
          Meter Details
        </h4>
        <MeterDetailsGrid meter={meter} />
      </div>
      
      <MeterReadingsSection 
        readingsOpen={readingsOpen}
        setReadingsOpen={handleReadingsToggle}
        readings={readings as PaginatedResponse<MeterReading>}
        loadingReadings={loadingReadings}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        hasBeenClicked={hasBeenClicked}
      />
    </div>
  );
};

// Sub-component for meter details grid
interface MeterDetailsGridProps {
  meter: any;
}

const MeterDetailsGrid: React.FC<MeterDetailsGridProps> = ({ meter }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Meter No:</span>
        <span>{meter.meterNumber}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Device No:</span>
        <span>{meter.deviceNo}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Meter Type:</span>
        <span>{meter.meterTypeDisplay}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Model:</span>
        <span>{meter.meterModel}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Assignment Date:</span>
        <span>{meter.currentReadingDate}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Installation Date:</span>
        <span>{meter.currentReadingDate}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Reading Status:</span>
        <span className="flex items-center">
          <span className="h-2 w-2 mr-1.5 rounded-full bg-green-500"></span>
          {meter.readingStatusDisplay}
        </span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Last Reading:</span>
        <span>{meter.lastReading} ({meter.lastReadingDate})</span>
      </div>
    </div>
  );
};

// Sub-component for meter readings section
interface MeterReadingsSectionProps {
  readingsOpen: boolean;
  setReadingsOpen: (open: boolean) => void;
  readings: PaginatedResponse<MeterReading>;
  loadingReadings: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  hasBeenClicked: boolean;
}

const MeterReadingsSection: React.FC<MeterReadingsSectionProps> = ({ 
  readingsOpen, 
  setReadingsOpen, 
  readings, 
  loadingReadings,
  currentPage,
  onPageChange,
  hasBeenClicked
}) => {
  return (
    <Collapsible open={readingsOpen} onOpenChange={setReadingsOpen}>
      <div className="p-4 rounded-md border">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto">
            <h4 className="font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Monthly Meter Readings
              {!hasBeenClicked && (
                <span className="ml-2 text-xs px-2 py-1 rounded">
                  Click to load
                </span>
              )}
            </h4>
            <ChevronDown className={`h-4 w-4 transition-transform ${readingsOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-3">
          {!hasBeenClicked ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Click the header above to load meter readings</p>
            </div>
          ) : loadingReadings ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2"></div>
            </div>
          ) : (
            <ReadingsTable 
              readings={readings} 
              currentPage={currentPage}
              onPageChange={onPageChange}
            />
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// Sub-component for readings table
interface ReadingsTableProps {
  readings: PaginatedResponse<MeterReading>;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const ReadingsTable: React.FC<ReadingsTableProps> = ({ readings, currentPage, onPageChange }) => {
  return (
    <div className="overflow-x-auto">
    <GenericTable
        data={readings?.results || []}
        columns={[
          { key: 'updatedOn', header: 'Date', type: 'text' },
          { 
            key: 'meterReading', 
            header: 'Reading', 
            type: 'text',
            render: (value) => `${value}`
          },
          {
            key: 'status',
            header: 'Status',
            type: 'badge',
            badgeConfig: {
              getVariant: (value) => 'default',
              getClassName: () => 'bg-green-100 text-green-800'
            }
          }
        ]}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((readings?.count || 0) / PAGE_SIZE),
          totalItems: readings?.count || 0,
          itemsPerPage: PAGE_SIZE,
          startItem: ((currentPage - 1) * PAGE_SIZE) + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, readings?.count || 0)
        }}
        onPageChange={onPageChange}
      />

      
    </div>
  );
};